/**
 * Utility functions for tracking login attempts
 */

const LOGIN_ATTEMPTED_KEY = 'loginAttempted';
const LOGIN_ATTEMPTED_AT_KEY = 'loginAttemptedAt';

/**
 * Checks if localStorage is available
 * @returns boolean - True if localStorage is available, false otherwise
 */
const isLocalStorageAvailable = (): boolean => {
  try {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  } catch {
    return false;
  }
};

/**
 * Marks that a login attempt was made by storing a marker and timestamp in localStorage
 * @returns void
 */
export const setLoginAttempted = (): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    localStorage.setItem(LOGIN_ATTEMPTED_KEY, 'true');
    localStorage.setItem(LOGIN_ATTEMPTED_AT_KEY, new Date().toISOString());
  } catch {
    // Silently fail if localStorage operations fail
  }
};

/**
 * Clears login attempt markers from localStorage
 * @returns void
 */
export const clearLoginAttemptMarkers = (): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    localStorage.removeItem(LOGIN_ATTEMPTED_KEY);
    localStorage.removeItem(LOGIN_ATTEMPTED_AT_KEY);
  } catch {
    // Silently fail if localStorage operations fail
  }
};

/**
 * Checks if a login attempt was made recently (within specified time window)
 * @param maxAgeMinutes Maximum age of login attempt in minutes (default: 10)
 * @returns boolean - True if a recent login attempt was made, false otherwise
 */
export const wasLoginAttempted = (maxAgeMinutes: number = 10): boolean => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  let loginAttempted = 'false';
  let loginAttemptedAt = '';
  try {
    loginAttempted = localStorage.getItem(LOGIN_ATTEMPTED_KEY) || loginAttempted;
    loginAttemptedAt = localStorage.getItem(LOGIN_ATTEMPTED_AT_KEY) || loginAttemptedAt;
  } catch {
    // If localStorage is not available or an error occurs, return false
    return false;
  }

  if (loginAttempted !== 'true') {
    return false;
  }

  if (!loginAttemptedAt) {
    return false;
  }

  const attemptTime = new Date(loginAttemptedAt);
  const maxAge = maxAgeMinutes * 60 * 1000; // Convert to milliseconds
  const now = new Date();
  const isStale = now.getTime() - attemptTime.getTime() > maxAge;

  if (isStale) {
    clearLoginAttemptMarkers();
    return false;
  }

  return true;
};
